import {
    ActionSpinOptions,
    ActionSpinSound,
    ASWinData, IResponseData,
    PromoPanelGameConfiguration
} from "@nolimitcity/promo-panel/bin/interfaces/ASInterfaces";
import {ASBonusPickOptions, ASMainGamePickOptions, WINTYPE} from "@nolimitcity/promo-panel/bin/enums/ASEnums";
import {IBlitzKriegGameData} from "./server/data/IBlitzKriegData";
import {BlitzKriegGameMode} from "./server/BlitzKriegGameMode";
import {BlitzKriegSound} from "./sound/BlitzKriegSound";
import {Helper} from "@nolimitcity/promo-panel/bin/utils/Helper";
import {DDay} from "./DDay";
import {MathHelper} from "../core/utils/MathHelper";
import {BlitzkriegSymbols} from "./symbol/DDaySymbolConfig";
import {BonusFeatureData} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/BonusFeatures";

export class DDayPromoPanelConfig implements PromoPanelGameConfiguration {
    private landedScatters = 0;

    constructor() {
    }

    public getActionSpinOptions(): ActionSpinOptions {
        return {
            hasActionSpin: true,
            gameName: "D-Day",
            mainGame: {
                header: () => "xBET",
                options: [
                    {name: "NORMAL", id: 0, image: "NORMAL.png"},
                    {name: "BOOSTED_BET", id: 1, image: "BOOSTED_BET.png"}
                ],
                type: ASMainGamePickOptions.BOOSTED_BET
            },
            automaticPickGame: {
                header: () => DDay.apiPlugin.translations.translate("Note: Features will be auto-selected during bonus trigger."), //TODO -- this needs a proper string.
                optionSelection: (response: any) => this.selectPick(response),
                type: ASBonusPickOptions.PICK_MODE,
                pickNeededForModes: [BlitzKriegGameMode.FS_PICK],
                pickNeededForBuyFeatures: [
                    "FREESPIN_3_SCATTERS",
                    "FREESPIN_4_SCATTERS",
                    "FREESPIN_5_SCATTERS",
                    "LUCKY_DRAW"
                ]
            },
            graphics: {
                backgroundTextureName: "introBg" //Loaded in intro view config.
            },
            getParsedData: (response: IBlitzKriegGameData, bet: number, prevResponse: IBlitzKriegGameData): ASWinData => {
                const parsedData: ASWinData = {
                    totalWin: response.accumulatedRoundWin,
                    wasFeatureBuy: response.wasFeatureBuy,
                    freeSpinTriggeredThisSpin: this.isFreespinTriggeredThisSpins(response),
                    totalSpinWinnings: response.totalSpinWinnings,
                    isBigWin: this.isBigWin(response),
                    winType: WINTYPE.NORMAL,
                    featureName: this.getFeatureName(response),
                    isRoundComplete: this.isRoundComplete(response),
                    isBonusEnd: this.isBonusEnd(response),
                    isWinCapHit: false,
                }
                return parsedData;
            }
        };
    }

    private selectPick(response: any): any {
        let selectedIndex: number = -1;
        while (selectedIndex == -1) {
            let randomPick = MathHelper.randomIntInRange(0, 4);
            if (response.fsPicks[randomPick] == -1) { //Means it was not picked before.
                selectedIndex = randomPick;
            }
        }
        return selectedIndex;
    }


    private isFreespinTriggeredThisSpins(response: IBlitzKriegGameData) {
        if (response.mode == BlitzKriegGameMode.NORMAL && response.nextMode == BlitzKriegGameMode.FS_PICK) {
            return true;
        }
        return false;
    }

    public onActionSpinPlaySound(trigger: ActionSpinSound): void {
        if (trigger == ActionSpinSound.WIN) {
            BlitzKriegSound.soundPlayer.playTrack({name: BlitzKriegSound.AS_WIN});
        } else if (trigger == ActionSpinSound.BIG_WIN) {
            BlitzKriegSound.soundPlayer.playTrack({name: BlitzKriegSound.AS_WIN_BIG});
        }
    }

    private isBigWin(response: IBlitzKriegGameData): boolean {
        const win = response.totalSpinWinnings;
        const playedBetValue = response.playedBetValue;
        if (response.wasFeatureBuy) {
            const betPrice = this.getBuyFeaturePrice();
            let winRatio: number = Helper.ceilToDecimals((win / betPrice), 2);
            return winRatio >= 1;
        }
        let winRatio: number = Helper.ceilToDecimals((win / playedBetValue), 2);
        return winRatio >= 15;
    }

    private getBuyFeaturePrice(): number {
        let betLevel = DDay.apiPlugin.freeBets.hasFreeBets() ? DDay.apiPlugin.freeBets.getBet() : DDay.apiPlugin.betLevel.getLevel();
        let price: number = DDay.model.gameData?.playedBetValue || parseFloat(betLevel);
        const featureData: BonusFeatureData | undefined = DDay.apiPlugin.bonusFeatures.getFeatureDataByName("BOOSTED_BET");
        if (DDay.model.data.wasFeatureBuy) {
            for (let index = 0; index < DDay.model.initData.featureBuyTimesBetValue.length; index++) {
                if (DDay.model.initData.featureBuyTimesBetValue[index].name === DDay.model.buyFeatureName) {
                    price *= DDay.model.initData.featureBuyTimesBetValue[index].price;
                    break;
                }
            }
        }
        return price;
    }

    private getFeatureName(response: IBlitzKriegGameData): string {
        if (this.isFreespinTriggeredThisSpins(response)) {
            response.evaluatedArea.forEach((reel: string[]) => {
                reel.forEach((sym: string) => {
                    if (sym === BlitzkriegSymbols.S) {
                        this.landedScatters++;
                    }
                });
            });
        }

        let name = "";
        if (this.landedScatters > 0) {
            if (this.landedScatters === 4) {
                name = "INVASION SPINS";
            } else if (this.landedScatters === 5) {
                name = "OVERLORD SPINS";
            } else {
                name = "NEPTUNE SPINS";
            }
        }

        if (response.nextMode == BlitzKriegGameMode.NORMAL) {
            this.landedScatters = 0;
        }

        return name;
    }

    isLegalStopOnBonusMode(data: IResponseData): boolean {
        return data.mode == BlitzKriegGameMode.NORMAL && data.nextMode == BlitzKriegGameMode.FS_PICK;
    }

    private isRoundComplete(response: IBlitzKriegGameData): boolean {
        return ([BlitzKriegGameMode.FREESPIN, BlitzKriegGameMode.SUPER_FREESPIN, BlitzKriegGameMode.NORMAL, BlitzKriegGameMode.FS_PICK].indexOf(response.nextMode) > -1);
    }

    private isBonusEnd(response: IBlitzKriegGameData): boolean {
        return response.nextMode === BlitzKriegGameMode.NORMAL;
    }
}
